// SSH public key validation for SFTP key-based authentication.
//
// Keys are parsed rather than regex-matched so that the key type and modulus
// size can be derived from the key material itself and minimum-strength rules
// can be enforced. The parser is intentionally dependency-free and isomorphic
// (no Buffer / node:crypto) so the same rules run at the API boundary and in
// the browser form.
//
// A note on "legacy SHA-1 ssh-rsa": `ssh-rsa` is the key-type prefix carried by
// every RSA public key in OpenSSH format. SHA-1 vs SHA-2 is a property of the
// *signature algorithm* negotiated at authentication time (`rsa-sha2-256` /
// `rsa-sha2-512`), and is not encoded in the stored key blob. Rejecting the
// `ssh-rsa` prefix would therefore reject all RSA keys. Key-level policy here
// is minimum RSA strength; SHA-2 signature preference belongs to the SSH
// endpoint configuration.
/** Key types accepted for SFTP authentication. */
export const SUPPORTED_SSH_PUBLIC_KEY_TYPES = [
    'ssh-rsa',
    'ecdsa-sha2-nistp256',
    'ecdsa-sha2-nistp384',
    'ecdsa-sha2-nistp521',
    'ssh-ed25519',
];
/** Minimum accepted RSA modulus size, in bits. */
export const MIN_RSA_KEY_BITS = 2048;
/**
 * Maximum accepted length of a single key entry.
 * Matches the `length.max` validation declared on the `ssh_public_keys`
 * attribute in the Keycloak user profile, so a key that passes here is always
 * storable.
 */
export const SSH_PUBLIC_KEY_MAX_LENGTH = 4096;
/**
 * Maximum number of keys stored against one user.
 *
 * Matches AWS's documented "SSH keys per Service Managed user" quota. We use a
 * custom identity provider rather than service-managed users, so this is a
 * reference ceiling rather than a hard external limit — but it is generous enough
 * that no legitimate user reaches it, and it bounds an attribute the SFTP IdP
 * Lambda re-reads on every login.
 *
 * @see https://docs.aws.amazon.com/general/latest/gr/transfer-service.html
 */
export const MAX_SSH_PUBLIC_KEYS = 50;
export const MAX_SSH_PUBLIC_KEYS_MESSAGE = `A user can have at most ${MAX_SSH_PUBLIC_KEYS} SSH public keys`;
const ECDSA_CURVE_BITS = {
    nistp256: 256,
    nistp384: 384,
    nistp521: 521,
};
const BASE64_REGEX = /^[A-Za-z0-9+/]+={0,2}$/;
const isSupportedType = (value) => SUPPORTED_SSH_PUBLIC_KEY_TYPES.includes(value);
const invalid = (error, message) => ({ isValid: false, error, message });
/**
 * Decodes standard base64 into bytes, rejecting anything that is not strictly
 * padded base64. `atob` alone is lenient, so the shape is checked first.
 */
const decodeBase64 = (value) => {
    if (value.length === 0 || value.length % 4 !== 0 || !BASE64_REGEX.test(value)) {
        return null;
    }
    try {
        const binary = atob(value);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i += 1) {
            bytes[i] = binary.charCodeAt(i);
        }
        return bytes;
    }
    catch {
        return null;
    }
};
/**
 * Reads one length-prefixed field (SSH wire format `string`: uint32 length
 * followed by that many bytes) starting at `offset`.
 */
const readField = (bytes, offset) => {
    if (offset + 4 > bytes.length)
        return null;
    const length = ((bytes[offset] << 24) |
        (bytes[offset + 1] << 16) |
        (bytes[offset + 2] << 8) |
        bytes[offset + 3]) >>>
        0;
    const start = offset + 4;
    if (length > bytes.length - start)
        return null;
    return { value: bytes.subarray(start, start + length), next: start + length };
};
const decodeAscii = (bytes) => Array.from(bytes, (byte) => String.fromCharCode(byte)).join('');
/**
 * Bit length of an SSH wire-format `mpint`. mpints are big-endian and carry a
 * leading zero byte when the high bit would otherwise mark them negative, so
 * leading zero bytes are skipped before measuring.
 */
const mpintBitLength = (mpint) => {
    let index = 0;
    while (index < mpint.length && mpint[index] === 0) {
        index += 1;
    }
    if (index === mpint.length)
        return 0;
    const firstByte = mpint[index];
    let highBit = 0;
    for (let bit = 7; bit >= 0; bit -= 1) {
        if (firstByte & (1 << bit)) {
            highBit = bit + 1;
            break;
        }
    }
    return (mpint.length - index - 1) * 8 + highBit;
};
/**
 * Validates a single OpenSSH-format public key entry and derives its type and
 * size.
 *
 * Accepts `<type> <base64-key> [comment]`. The type declared in the entry must
 * match the type embedded in the key blob, which is what makes this a real
 * parse rather than a format check.
 *
 * @example
 * validateSshPublicKey('ssh-ed25519 AAAAC3Nza... user@host')
 * // { isValid: true, info: { type: 'ssh-ed25519', bits: 256, comment: 'user@host' } }
 * validateSshPublicKey('ssh-dss AAAAB3Nza...')
 * // { isValid: false, error: 'unsupported_type', ... }
 */
export const validateSshPublicKey = (rawKey) => {
    const key = (rawKey ?? '').trim();
    if (key === '') {
        return invalid('empty', 'SSH public key is required');
    }
    if (key.length > SSH_PUBLIC_KEY_MAX_LENGTH) {
        return invalid('too_long', `SSH public key must be at most ${SSH_PUBLIC_KEY_MAX_LENGTH} characters`);
    }
    if (key.includes('\n') || key.includes('\r')) {
        return invalid('malformed', 'Each SSH public key must be a single line');
    }
    if (key.startsWith('-----BEGIN')) {
        return invalid('private_key', 'This looks like a PEM-encoded key file, not an OpenSSH public key. Paste the contents of your .pub file');
    }
    const parts = key.split(/\s+/);
    if (parts.length < 2) {
        return invalid('malformed', 'SSH public key must be in OpenSSH format: "<type> <base64-key> [comment]"');
    }
    const declaredType = parts[0];
    if (!isSupportedType(declaredType)) {
        if (declaredType === 'ssh-dss') {
            return invalid('unsupported_type', 'DSA keys (ssh-dss) are not supported');
        }
        return invalid('unsupported_type', `Unsupported SSH key type "${declaredType}". Supported types: ${SUPPORTED_SSH_PUBLIC_KEY_TYPES.join(', ')}`);
    }
    const blob = decodeBase64(parts[1]);
    if (!blob) {
        return invalid('invalid_base64', 'SSH public key body is not valid base64');
    }
    const typeField = readField(blob, 0);
    if (!typeField) {
        return invalid('malformed', 'SSH public key body is truncated or malformed');
    }
    if (decodeAscii(typeField.value) !== declaredType) {
        return invalid('type_mismatch', `SSH public key body does not match its declared type "${declaredType}"`);
    }
    const comment = parts.length > 2 ? parts.slice(2).join(' ') : null;
    const valid = (bits) => ({
        isValid: true,
        info: { type: declaredType, bits, comment },
    });
    if (declaredType === 'ssh-rsa') {
        const exponent = readField(blob, typeField.next);
        const modulus = exponent ? readField(blob, exponent.next) : null;
        if (!modulus) {
            return invalid('malformed', 'RSA public key is truncated or malformed');
        }
        const bits = mpintBitLength(modulus.value);
        if (bits < MIN_RSA_KEY_BITS) {
            return invalid('weak_key', `RSA keys must be at least ${MIN_RSA_KEY_BITS} bits (this key is ${bits} bits)`);
        }
        return valid(bits);
    }
    if (declaredType === 'ssh-ed25519') {
        const point = readField(blob, typeField.next);
        if (!point || point.value.length !== 32) {
            return invalid('malformed', 'ed25519 public key is truncated or malformed');
        }
        return valid(256);
    }
    // ECDSA: the curve name is repeated inside the blob and must agree with the
    // curve named in the key type.
    const curveField = readField(blob, typeField.next);
    if (!curveField) {
        return invalid('malformed', 'ECDSA public key is truncated or malformed');
    }
    const curveName = decodeAscii(curveField.value);
    const expectedCurve = declaredType.slice('ecdsa-sha2-'.length);
    if (curveName !== expectedCurve) {
        return invalid('type_mismatch', `ECDSA curve "${curveName}" does not match its declared type "${declaredType}"`);
    }
    const pointField = readField(blob, curveField.next);
    if (!pointField || pointField.value.length === 0) {
        return invalid('malformed', 'ECDSA public key is truncated or malformed');
    }
    return valid(ECDSA_CURVE_BITS[curveName]);
};
/**
 * Returns a user-friendly error message for a single SSH public key, or null
 * when the key is valid. Mirrors `getPhoneNumberError` for use in UI forms.
 */
export const getSshPublicKeyError = (rawKey) => {
    const result = validateSshPublicKey(rawKey);
    return result.isValid ? null : (result.message ?? 'Invalid SSH public key');
};
/**
 * Whether a single SSH public key entry is valid.
 */
export const isValidSshPublicKey = (rawKey) => validateSshPublicKey(rawKey).isValid;
/**
 * Normalizes a list of SSH public key entries for storage.
 *
 * Splits any newline-separated entries, trims each, drops blanks, and removes
 * duplicates. De-duplication compares the type and key body only, so re-adding
 * the same key under a different comment does not create a second entry.
 * Order is preserved, keeping the first occurrence.
 */
export const normalizeSshPublicKeys = (keys) => {
    const seen = new Set();
    const normalized = [];
    for (const entry of keys) {
        for (const line of entry.split(/\r?\n/)) {
            const key = line.trim();
            if (key === '')
                continue;
            const parts = key.split(/\s+/);
            const identity = parts.length >= 2 ? `${parts[0]} ${parts[1]}` : key;
            if (seen.has(identity))
                continue;
            seen.add(identity);
            normalized.push(key);
        }
    }
    return normalized;
};
/**
 * Formats a parsed key for display, e.g. "ED25519 256-bit".
 */
export const describeSshPublicKey = (info) => {
    if (info.type === 'ssh-ed25519')
        return `ED25519 ${info.bits}-bit`;
    if (info.type === 'ssh-rsa')
        return `RSA ${info.bits}-bit`;
    return `ECDSA ${info.bits}-bit`;
};
//# sourceMappingURL=ssh-public-key.util.js.map